import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import Search from '@fuww/library/src/Jobs/Search';
import HiddenLoader from '@fuww/library/src/HiddenLoader';
import { useSiteContext } from '../../SiteContext';
import filterQuery from '../../../lib/filterQuery';
import withQuery from '../../../lib/withQuery';
import replaceRoute from '../../../lib/replaceRoute';
import getJobsRoute from '../../../lib/getJobsRoute';
import cityFiltersQuery
  from '../../../lib/queries/jobs/cityFiltersQuery.graphql';

interface City {
  name: string;
  slug: string;
}

interface JobsQuery {
  citySlug?: string;
  companySlug?: string;
  positionSlug?: string;
  locationSearch?: string;
}

const getCityIsSelected = (
  city: City,
  query: JobsQuery,
) => {
  const { name, slug } = city;
  const {
    citySlug,
    companySlug,
    positionSlug,
    locationSearch,
  } = query;
  if (companySlug || positionSlug) {
    return name === locationSearch;
  }
  return slug === citySlug;
};

const getCityClickHandler = (
  city: City,
  query: JobsQuery,
  selected: boolean,
  locale: string,
  isCareerPage: boolean,
) => {
  const {
    name,
    slug,
  } = city;
  const {
    companySlug,
    positionSlug,
  } = query;

  const updatedQuery = {
    ...query,
    ...(companySlug || positionSlug) ? {
      locationSearch: selected ? null : name,
      citySlug: null,
    } : {
      locationSearch: null,
      citySlug: selected ? null : slug,
    },
    sort: null,
    distanceFrom: null,
  };

  return () => replaceRoute(
    getJobsRoute(updatedQuery, locale, isCareerPage),
    updatedQuery,
  );
};

const jobSearchFiltersProperties = {
  keywords: PropTypes.string,
  searchFieldTitle: PropTypes.string.isRequired,
  onSearchTrailingIconSelect: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  locationSearch: PropTypes.string,
  locationSearchFieldTitle: PropTypes.string.isRequired,
  onLocationSearchTrailingIconSelect: PropTypes.func.isRequired,
  onLocationSearchChange: PropTypes.func.isRequired,
  categoryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      selected: PropTypes.bool.isRequired,
    }).isRequired,
  ).isRequired,
  showJobsPageLoader: PropTypes.bool.isRequired,
  showLoader: PropTypes.bool.isRequired,
  onAllLocalesFilterClick: PropTypes.func,
  showJobsForAllLocales: PropTypes.bool,
  allLocalesCheckboxLabel: PropTypes.string.isRequired,
  sortByDistanceInsertion: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      onClick: PropTypes.func.isRequired,
      jobsCount: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  jobsFilterVariables: PropTypes.shape({
    locales: PropTypes.arrayOf(PropTypes.string),
    profileId: PropTypes.string,
    profileIdsOrKeywords: PropTypes.shape({
      profileIds: PropTypes.arrayOf(PropTypes.string),
      keywords: PropTypes.string,
    }),
    positionId: PropTypes.string,
  }).isRequired,
  jobsNearMeSelected: PropTypes.bool,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const jobSearchFiltersWithCitiesProperties = {
  ...jobSearchFiltersProperties,
  data: PropTypes.shape({
    cities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        jobsCount: PropTypes.number.isRequired,
      }).isRequired,
    ).isRequired,
  }).isRequired,
  onJobsNearMeClick: PropTypes.func.isRequired,
  jobsNearMeSelected: PropTypes.bool,
};

type JobSearchFiltersWithCitiesProperties = InferProps<
typeof jobSearchFiltersWithCitiesProperties
>;

const JobSearchFiltersWithCities = withQuery(({
  data,
  searchFieldTitle,
  locationSearchFieldTitle,
  keywords,
  onSearchChange,
  onSearchTrailingIconSelect,
  locationSearch,
  onLocationSearchChange,
  onLocationSearchTrailingIconSelect,
  categoryOptions,
  showJobsPageLoader,
  showLoader,
  onAllLocalesFilterClick,
  showJobsForAllLocales,
  allLocalesCheckboxLabel,
  sortByDistanceInsertion,
  onJobsNearMeClick,
  jobsNearMeSelected,
}: JobSearchFiltersWithCitiesProperties) => {
  const { locale, isCareerPage } = useSiteContext();
  const { query } = filterQuery(useRouter());

  const { cities = [] } = { ...data };

  return (
    <Search
      cities={
        cities.map(
          (city) => {
            const { id, name, jobsCount } = city;
            const selected = getCityIsSelected(city, query);
            return {
              id,
              name,
              jobsCount,
              onClick: getCityClickHandler(
                city,
                query,
                selected,
                locale,
                isCareerPage,
              ),
              selected,
            };
          },
        )
      }
      sortByDistanceInsertion={sortByDistanceInsertion}
      searchFieldTitle={searchFieldTitle}
      locationSearchFieldTitle={locationSearchFieldTitle}
      keywords={keywords}
      onSearchChange={onSearchChange}
      onSearchTrailingIconSelect={onSearchTrailingIconSelect}
      locationSearch={locationSearch}
      onLocationSearchChange={onLocationSearchChange}
      onLocationSearchTrailingIconSelect={onLocationSearchTrailingIconSelect}
      categoryOptions={categoryOptions}
      showJobsPageLoader={showJobsPageLoader}
      showCitiesLoader={showLoader}
      onAllLocalesFilterClick={onAllLocalesFilterClick ?? undefined}
      showJobsForAllLocales={showJobsForAllLocales}
      allLocalesCheckboxLabel={allLocalesCheckboxLabel}
      onJobsNearMeClick={onJobsNearMeClick}
      jobsNearMeSelected={Boolean(jobsNearMeSelected)}
    />
  );
}, { renderAlways: true });

type JobSearchFiltersProperties = InferProps<
typeof jobSearchFiltersProperties
>;

const JobSearchFilters = ({
  jobsFilterVariables,
  showJobsPageLoader,
  showJobsForAllLocales,
  ...properties
}: JobSearchFiltersProperties) => {
  const { jobsLocale } = useSiteContext();
  return (
    <JobSearchFiltersWithCities
      query={cityFiltersQuery}
      variables={{
        ...jobsFilterVariables,
        jobsFilter: jobsFilterVariables,
        locales: showJobsForAllLocales ? null : [jobsLocale],
        limit: 6,
        sort: 'ACTIVE_JOBS_COUNT_DESCENDING',
      }}
      errorMessage="Problem loading city filters"
      loader={<HiddenLoader />}
      skip={showJobsPageLoader}
      showLoaderWhenSetVariables={false}
      showJobsForAllLocales={showJobsForAllLocales}
      {...properties}
    />
  );
};

export default JobSearchFilters;
